// ABOUTME: Web Speech API wrapper for voice input functionality
// ABOUTME: Handles browser compatibility and provides clean interface for speech recognition

import { debug } from '@/lib/utils';

export interface SpeechToTextResult {
  transcript: string;
  isFinal: boolean;
  confidence: number;
}

export interface SpeechToTextOptions {
  continuous?: boolean;
  interimResults?: boolean;
  language?: string;
  maxAlternatives?: number;
}

// Extend Window interface for speech recognition
interface WindowWithSpeechRecognition extends Window {
  SpeechRecognition?: new () => SpeechRecognition;
  webkitSpeechRecognition?: new () => SpeechRecognition;
}

export class SpeechToTextService {
  private recognition: SpeechRecognition | null = null;
  private isListening = false;

  constructor() {
    this.initializeRecognition();
  }

  private initializeRecognition() {
    // Check for browser support
    const SpeechRecognition = (window as WindowWithSpeechRecognition).SpeechRecognition ||
                             (window as WindowWithSpeechRecognition).webkitSpeechRecognition;

    if (!SpeechRecognition) {
      debug.warn('Speech recognition not supported in this browser');
      return;
    }

    this.recognition = new SpeechRecognition();
  }

  isSupported(): boolean {
    return this.recognition !== null;
  }

  async startListening(
    onResult: (result: SpeechToTextResult) => void,
    onError: (error: string) => void,
    options: SpeechToTextOptions = {}
  ): Promise<void> {
    if (!this.recognition) {
      onError('Speech recognition not supported');
      return;
    }

    if (this.isListening) {
      debug.warn('Already listening');
      return;
    }

    // Configure recognition
    this.recognition.continuous = options.continuous ?? true;
    this.recognition.interimResults = options.interimResults ?? true;
    this.recognition.lang = options.language ?? 'en-GB';
    this.recognition.maxAlternatives = options.maxAlternatives ?? 1;

    // Set up event handlers
    this.recognition.onresult = (event) => {
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const transcript = result[0].transcript;
        const confidence = result[0].confidence;

        onResult({
          transcript,
          isFinal: result.isFinal,
          confidence: confidence || 0.5
        });
      }
    };

    this.recognition.onerror = (event) => {
      debug.error('Speech recognition error:', event.error);
      this.isListening = false;

      let errorMessage = 'Speech recognition failed';
      switch (event.error) {
        case 'no-speech':
          errorMessage = 'No speech detected. Please try again.';
          break;
        case 'audio-capture':
          errorMessage = 'Microphone not accessible. Please check permissions.';
          break;
        case 'not-allowed':
          errorMessage = 'Microphone permission denied. Please allow access and try again.';
          break;
        case 'network':
          errorMessage = 'Network error. Please check your connection.';
          break;
        default:
          errorMessage = `Speech recognition error: ${event.error}`;
      }

      onError(errorMessage);
    };

    this.recognition.onend = () => {
      debug.log('Speech recognition ended');
      this.isListening = false;
    };

    this.recognition.onstart = () => {
      debug.log('Speech recognition started');
      this.isListening = true;
    };

    try {
      this.recognition.start();
    } catch (error) {
      debug.error('Failed to start speech recognition:', error);
      onError('Failed to start speech recognition');
    }
  }

  stopListening(): void {
    if (this.recognition && this.isListening) {
      this.recognition.stop();
    }
  }

  abort(): void {
    if (this.recognition && this.isListening) {
      this.recognition.abort();
      this.isListening = false;
    }
  }

  getIsListening(): boolean {
    return this.isListening;
  }
}

// Export a singleton instance
export const speechToTextService = new SpeechToTextService();
